import { ChangeEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { downloadFile } from '../../helpers/FileDownload'
import allTranslationKeys from './../../i18n/translations/keys/i18n/en/translation.json'
import './Translations.scss'

const languages = ['en', 'de', 'it', 'fr'] as const
const keys = Object.keys(allTranslationKeys).sort((a, b) => a.localeCompare(b))

type Language = (typeof languages)[number]
type Dictionary = Record<Language, Record<string, string | undefined>>

export function Translations() {
  const [translations, setTranslations] = useState<Dictionary>({ en: {}, de: {}, fr: {}, it: {} })

  const { t } = useTranslation('common')

  const updateEntry = (e: ChangeEvent<HTMLInputElement>, language: Language, key: string) => {
    setTranslations({
      ...translations,
      [language]: {
        ...translations[language],
        [key]: e.target.value,
      },
    })
  }

  const downloadLanguage = (language: Language) => {
    const result: Record<string, string | undefined> = {}

    for (const key in allTranslationKeys) {
      const value = translations[language][key] ?? t(key, { lng: language })
      if (value && value !== key) {
        result[key] = value
      }
    }

    const blob = new Blob([JSON.stringify(result, null, '\t')], { type: 'application/json' })

    downloadFile({
      fileURL: URL.createObjectURL(blob),
      filename: `${language}.json`,
    })
  }

  return (
    <div className="translations">
      <h3>{t('APP_TRANSLATIONS')}</h3>
      <br />
      <table>
        <thead>
          <tr>
            <th>{t('KEY')}</th>
            {languages.map((lang) => (
              <th key={lang}>{lang}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {keys.map((key) => (
            <tr key={key}>
              <td>{key}</td>
              {languages.map((lang) => (
                <td key={lang}>
                  <input
                    type="text"
                    name={`fieldnameveryunique${new Date().getTime()}`}
                    autoComplete="none"
                    list="autocompleteOff"
                    className="translation-input"
                    placeholder={key}
                    value={translations[lang][key] ?? t(key, { lng: lang })}
                    onChange={(e) => updateEntry(e, lang, key)}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td>&nbsp;</td>
            {languages.map((lang) => (
              <td key={lang}>
                <button onClick={() => downloadLanguage(lang)}>{`${t('DOWNLOAD_JSON')} (${lang})`}</button>
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  )
}
